import React, { useState, useEffect } from "react";
import { LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { api } from "../../utils/api";

const monthNames = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

export default function IncomeReport() {
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [totals, setTotals] = useState({ tahsilat: 0, alacak: 0 });

  useEffect(() => {
    Promise.all([
      api.get("/dashboard"),
      api.get("/transactions?kind=income"),
      api.get("/owners").catch(() => ({ data: [] }))
    ]).then(([dashRes, txsRes, ownersRes]) => {
      const ap = dashRes.data.active_period;
      const actualCurrentPending = dashRes.data.pending_dues || 0; 
      const txs = txsRes.data;
      const owners = ownersRes.data;

      if (!ap || !ap.start_date || !ap.end_date) {
        setLoading(false);
        return;
      }

      // VİLLA SAYISI VE AYLIK BEKLENEN AİDAT HESABI
      const totalVillas = dashRes.data.owner_count || 91;
      let exemptVillas = 0;
      let payingVillas = totalVillas;
      
      if (owners && owners.length > 0) {
        // aidat_oder_mi değeri açıkça false olanları sayıyoruz
        exemptVillas = owners.filter(o => o.aidat_oder_mi === false).length;
        payingVillas = totalVillas - exemptVillas;
      } else {
        // Fallback: API'den owners gelmezse manuel 3 kişi muaf say
        exemptVillas = 3;
        payingVillas = totalVillas > 3 ? totalVillas - 3 : totalVillas;
      }

      const monthlyFee = ap.due_1 || 2500; 
      const totalGrossMonthly = totalVillas * monthlyFee; // 91 * 2500 = 227.500
      const exemptMonthlyTotal = exemptVillas * monthlyFee; // 3 * 2500 = 7.500
      const expectedMonthlyTotal = payingVillas * monthlyFee; // 88 * 2500 = 220.000

      const sDate = new Date(ap.start_date);
      const eDate = new Date(ap.end_date);
      
      setPeriodInfo({
        name: ap.name,
        start: sDate,
        end: eDate,
        totalVillas,
        exemptVillas,
        monthlyFee,
        totalGrossMonthly,
        exemptMonthlyTotal,
        expectedMonthlyTotal,
        payingVillas
      });

      const today = new Date();
      const chartEndDate = eDate > today ? today : eDate;

      // 1. Ay şablonunu oluştur
      const monthlyObj = {};
      let currentMonth = new Date(sDate.getFullYear(), sDate.getMonth(), 1);
      const endLimit = new Date(chartEndDate.getFullYear(), chartEndDate.getMonth(), 1);

      while (currentMonth <= endLimit) {
        const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = `${monthNames[currentMonth.getMonth()]}'${currentMonth.getFullYear().toString().slice(2)}`;
        monthlyObj[monthKey] = { key: monthKey, name: monthLabel, tahsilat: 0, alacak: 0 };
        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }

      let totalTahsilat = 0;

      // 2. Tahsilatları aylara göre dağıt
      txs.forEach(tx => {
        if (!tx.date) return;
        const txDate = new Date(tx.date);

        if (txDate >= sDate && txDate <= eDate) {
          const amt = Number(tx.amount) || 0;
          totalTahsilat += amt;

          const monthKey = `${txDate.getFullYear()}-${String(txDate.getMonth() + 1).padStart(2, '0')}`;
          if (monthlyObj[monthKey]) {
            monthlyObj[monthKey].tahsilat += amt;
          }
        }
      });

      // 3. KÜMÜLATİF ALACAK HESAPLAMASI
      const monthKeys = Object.keys(monthlyObj).sort();
      const totalGeneratedDebt = monthKeys.length * expectedMonthlyTotal; 
      
      const devredenAlacak = actualCurrentPending - (totalGeneratedDebt - totalTahsilat);
      let runningAlacak = devredenAlacak; 

      monthKeys.forEach(key => {
        runningAlacak = runningAlacak + expectedMonthlyTotal - monthlyObj[key].tahsilat;
        monthlyObj[key].alacak = runningAlacak > 0 ? runningAlacak : 0; 
      });

      const mData = Object.values(monthlyObj).sort((a, b) => a.key.localeCompare(b.key));
      setMonthlyData(mData);
      
      // 4. Pasta Grafik Verisi
      setPieData([
        { name: "Tahsilat", value: totalTahsilat, color: "#10b981" }, 
        { name: "Alacaklar", value: actualCurrentPending, color: "#f97316" } 
      ]);
      
      setTotals({ tahsilat: totalTahsilat, alacak: actualCurrentPending });
      setLoading(false);
    }).catch(err => {
      console.error("Gelir raporu verileri çekilemedi:", err);
      setLoading(false);
    });
  }, []);

  const formatTL = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(value);

  const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25; 
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill={pieData[index]?.color} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="15" fontWeight="bold">
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  if (loading) return <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Gelir ve Alacak verileri hesaplanıyor...</div>;

  if (!periodInfo) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#ef4444", background: "white", borderRadius: "16px", border: "1px solid #fee2e2" }}>
      <h3>Aktif Dönem Bulunamadı!</h3>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.4s ease-out" }}>
      
      {/* ÜST PANO */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "24px 30px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", gap: "20px" }}>
        <div>
          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#10b981", letterSpacing: "1px", textTransform: "uppercase" }}>AKTİF DÖNEM GELİR & ALACAK RAPORU</span>
          <h2 style={{ margin: "6px 0", fontSize: "22px", color: "#1e293b" }}>{periodInfo.name} Dönemi</h2>
          
          {/* YENİ DİNAMİK TAHAKKUK METNİ */}
          <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
            Aylık Tahakkuk: <strong>{periodInfo.totalVillas} Hane</strong> × {formatTL(periodInfo.monthlyFee)} = <strong style={{color: "#1e293b"}}>{formatTL(periodInfo.totalGrossMonthly)}</strong>
            {periodInfo.exemptVillas > 0 && (
              <>
                {" "} - ({periodInfo.exemptVillas} Yönetici x {formatTL(periodInfo.monthlyFee)}) = <strong style={{color: "#1e293b"}}>{formatTL(periodInfo.expectedMonthlyTotal)}</strong>
              </>
            )}
          </p>

        </div>
        <div style={{ display: "flex", gap: "40px", textAlign: "right" }}>
          <div style={{ borderLeft: "2px solid #f1f5f9", paddingLeft: "20px" }}>
            <span style={{ fontSize: "13px", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>Toplam Tahsilat</span>
            <div style={{ fontSize: "26px", fontWeight: "bold", color: "#10b981", marginTop: "4px" }}>{formatTL(totals.tahsilat)}</div>
          </div>
          <div style={{ borderLeft: "2px solid #f1f5f9", paddingLeft: "20px" }}>
            <span style={{ fontSize: "13px", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>Toplam Alacak</span>
            <div style={{ fontSize: "26px", fontWeight: "bold", color: "#f97316", marginTop: "4px" }}>{formatTL(totals.alacak)}</div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "24px" }}>
        
        {/* SOL KART: Aylara Göre Tahsilat (Çizgi Grafik) */}
        <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "16px", color: "#1e293b" }}>Aylara Göre Tahsilat Eğilimi</h3>
          <div style={{ height: "300px", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} dy={10} />
                <YAxis tickFormatter={(val) => `₺${val}`} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(value) => [formatTL(value), "Tahsilat"]} cursor={{ stroke: "#e2e8f0", strokeWidth: 2 }} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Line type="monotone" dataKey="tahsilat" stroke="#10b981" strokeWidth={4} dot={{ r: 4, fill: "#10b981", strokeWidth: 2, stroke: "white" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* SAĞ KART: Tahsilat vs Alacak (% Etiketli Pasta Grafik) */}
        <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "16px", color: "#1e293b" }}>Tahsilat & Alacak Dağılımı</h3>
          <div style={{ height: "300px", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie 
                  data={pieData} 
                  cx="50%" cy="50%" 
                  innerRadius={0} 
                  outerRadius={90} 
                  dataKey="value" 
                  nameKey="name" 
                  stroke="white" 
                  strokeWidth={3}
                  label={renderCustomizedLabel} 
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatTL(value), "Tutar"]} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "14px", color: "#475569", fontWeight: "500", marginTop: "10px" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* ALT KART: Aylık Detay Listesi Tablosu */}
      <div style={{ background: "white", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
        <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "16px", color: "#1e293b" }}>Aylık Tahsilat ve Bakiye Alacak Özeti</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0", color: "#64748b", fontSize: "13px" }}>
              <th style={{ padding: "12px 8px" }}>Ay</th>
              <th style={{ padding: "12px 8px", textAlign: "right" }}>Aylık Tahsilat</th>
              <th style={{ padding: "12px 8px", textAlign: "right" }}>O Ayki Toplam Alacak (Kümülatif)</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.length === 0 ? (
              <tr><td colSpan="3" style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>Bu dönemde veri bulunmuyor.</td></tr>
            ) : monthlyData.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f8fafc", transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: "14px 8px", fontWeight: "600", color: "#334155" }}>
                  {row.name}
                </td>
                <td style={{ padding: "14px 8px", textAlign: "right", fontWeight: "bold", color: "#10b981" }}>
                  {formatTL(row.tahsilat)}
                </td>
                <td style={{ padding: "14px 8px", textAlign: "right", fontWeight: "bold", color: "#f97316" }}>
                  {formatTL(row.alacak)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}